import React, { memo, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';

import { Button, Icons, Modal, SpinnerOverlay } from '@components/common';
import {
	TOnApprove,
} from '@components/routes/AdminPanel/AssetsRent/RequestReservationCard/RequestActionConfirmationModal/RequestActionConfirmationModal';

import { useMutateAcceptAssetRentRequestCard } from '@hooks/assets/useMutateAcceptAssetRentRequestCard';
import { useMutateContractAssetRentRequest } from '@hooks/assets/useMutateContractAssetRentRequest';
import { useMutateDeclineRentRequest } from '@hooks/assets/useMutateDeclineRentRequest';
import { useMutateDoneAssetRentRequest } from '@hooks/assets/useMutateDoneAssetRentRequest';
import { useMutateProposeDates } from '@hooks/assets/useMutateProposeDates';
import { useMutateSendRequestItemToAO } from '@hooks/assets/useMutateSendRequestItemToAO';
import { useMutateSendRequestItemToMNO } from '@hooks/assets/useMutateSendRequestItemToMNO';
import { useQueryAssetRentCard } from '@hooks/assets/useQueryAssetRentCard';
import { useQueryAssetRequestDocuments } from '@hooks/assets/useQueryAssetRequestDocuments';

import { auth } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { TSelectOption } from '@interfaces/components/componentTypes';

import { ECommonRequestStatusesForAssetRequest } from '@enums/asset';

import { ERoles } from './constants';

import { IProposeNewDatesParams, TProposeNewDatesForItemParams } from './ProposeNewDatesForItem/ProposeNewDatesForItem';
import AttachedContractDocuments from './AttachedContractDocuments';
import ProposeNewDatesForItem from './ProposeNewDatesForItem';
import RequestActionConfirmationModal from './RequestActionConfirmationModal';
import RequestDescription from './RequestDescription';
import RequesterBlock from './RequesterBlock';
import RequestTable from './RequestTable';
import { checkIsAbleToDecline, checkIsAbleToViewAttachedDocuments } from './utils';

import styles from './RequestReservationCard.module.scss';

interface IRequestReservationCardProps {
	role: string;
}

const RequestReservationCard = ({
	role,
}: IRequestReservationCardProps) => {
	const navigate = useNavigate();
	const { id = '' } = useParams();
	const { accessPermission } = useAppSelector(auth);

	const [isDeclineModalOpen, setIsDeclineModalOpen] = useState<boolean>(false);
	const [isProposeDatesModal, setIsProposeDatesModal] = useState<boolean>(false);
	const [isShowDocumentsTable, setIsShowDocumentsTable] = useState<boolean>(false);
	const [selectedItemForChangeDates, setSelectedItemForChangeDates] = useState<TSelectOption | undefined>();

	const onBackClickHandler = useCallback(() => navigate(-1), [navigate]);

	const isAo = role === ERoles.ao;

	const {
		request,
		isLoading,
		reFetchRequestCard,
	} = useQueryAssetRentCard(id);

	const { declineRequest } = useMutateDeclineRentRequest(id, [reFetchRequestCard]);
	const { proposeDatesForItem } = useMutateProposeDates(id, [reFetchRequestCard]);
	const { sendRequestToAO } = useMutateSendRequestItemToAO(id, [reFetchRequestCard]);
	const { sendRequestToMNO } = useMutateSendRequestItemToMNO(id, [reFetchRequestCard]);
	const { acceptAssetRentRequestCard } = useMutateAcceptAssetRentRequestCard(id, [reFetchRequestCard]);
	const { contractAssetRentRequest } = useMutateContractAssetRentRequest(id, [reFetchRequestCard]);
	const { doneAssetRentRequest } = useMutateDoneAssetRentRequest(id, [reFetchRequestCard]);

	const requestStatus = request?.requestStatus?.name;

	const isContract = requestStatus === ECommonRequestStatusesForAssetRequest.contract;
	const isDocumentsEditAccess = isContract && accessPermission.assetOwnerSignatory;

	const isAbleToDecline = checkIsAbleToDecline({
		role,
		accessPermission,
		requestStatus: requestStatus,
	});

	const isAbleToViewAttachedDocuments = checkIsAbleToViewAttachedDocuments({
		role,
		accessPermission,
		requestStatus: requestStatus,
	});

	const { count } = useQueryAssetRequestDocuments({
		requestId: request?.id,
		isAo,
		isDocumentsAccess: isAbleToViewAttachedDocuments,
	});

	useEffect(() => {
		if (!request && !isLoading) {
			onBackClickHandler();
		}
	}, [isLoading, navigate, onBackClickHandler, request]);

	const closeDeclineModal = useCallback(() => {
		setIsDeclineModalOpen(false);
	}, []);

	const openDeclineModal = useCallback(() => {
		setIsDeclineModalOpen(true);
	}, []);

	const confirmDecline = useCallback(async (comment: string) => {
		try {
			await declineRequest(comment);

			closeDeclineModal();
		} catch (e) {
			return;
		}
	}, [closeDeclineModal, declineRequest]);

	const openProposeDatesModal = useCallback((selectedItem: TSelectOption) => {
		setSelectedItemForChangeDates(selectedItem);
		setIsProposeDatesModal(true);
	}, []);

	const closeProposeDatesModal = useCallback(() => setIsProposeDatesModal(false), []);

	const confirmNewDateProposition = useCallback(async ({
		requestDbId,
		itemId,
		startDate,
		endDate,
		comment,
	}: IProposeNewDatesParams & { requestDbId: string }) => {
		try {
			await proposeDatesForItem({
				requestDbId,
				itemId,
				startDate,
				endDate,
				comment,
			});

			closeProposeDatesModal();
		} catch (e) {
			return;
		}
	}, [closeProposeDatesModal, proposeDatesForItem]);

	const [isSendToAOModal, setIsSendToAOModal] = useState<boolean>(false);
	const [isSendToMNOModal, setIsSendToMNOModal] = useState<boolean>(false);
	const [isAcceptModalOpen, setIsAcceptModalOpen] = useState<boolean>(false);
	const [isContractModalOpen, setIsContractModalOpen] = useState<boolean>(false);
	const [isDoneModalOpen, setIsDoneModalOpen] = useState<boolean>(false);

	const openSendToAOModal = () => setIsSendToAOModal(true);
	const openSendToMNOModal = () => setIsSendToMNOModal(true);

	const openAcceptModal = () => setIsAcceptModalOpen(true);
	const closeAcceptModal = () => setIsAcceptModalOpen(false);

	const openContractModal = () => setIsContractModalOpen(true);
	const closeContractModal = () => setIsContractModalOpen(false);

	const openDoneModal = () => setIsDoneModalOpen(true);
	const closeDoneModal = () => setIsDoneModalOpen(false);

	const openAttachedDocumentsTable = () => setIsShowDocumentsTable(true);
	const closeAttachedDocumentsTable = () => setIsShowDocumentsTable(false);

	const acceptAssetRentRequest = (comment: string) => {
		acceptAssetRentRequestCard({ id: request.id, isAO: isAo, comment });

		closeAcceptModal();
	};

	const contractAssetRentRequestHandler = (comment: string) => {
		contractAssetRentRequest({ id: request.id, comment });

		closeContractModal();
	};

	const doneAssetRentRequestHandler = (comment: string) => {
		doneAssetRentRequest({ id: request.id, comment });

		closeDoneModal();
	};

	const closeSendToMNOModal = () => setIsSendToMNOModal(false);
	const closeSendToAOModal = () => setIsSendToAOModal(false);

	const handleSendToAO = useCallback((comment: string) => {
		sendRequestToAO(comment);
		closeSendToAOModal();
	}, [sendRequestToAO]);

	const handleSendToMNO = useCallback((comment: string) => {
		sendRequestToMNO(comment);
		closeSendToMNOModal();
	}, [sendRequestToMNO]);

	return (
		<div className={styles.requestCardContainer}>
			{request && <>
				<Button
					icon={Icons.arrowLeft()}
					onClick={onBackClickHandler}
					text='Back'
					withoutBackground
				/>
				<div className={styles.descriptionAndRequesterBlock}>
					<RequestDescription
						attachedDocumentsCount={count}
						comment={request?.comment}
						creationDate={request.creationDate}
						isAbleToDecline={isAbleToDecline}
						isAttachDocuments={isAbleToViewAttachedDocuments}
						openAttachedDocuments={openAttachedDocumentsTable}
						openDeclineModal={openDeclineModal}
						requestId={request.requestId}
						requestType={request.type}
						role={role}
						updateDate={request.updateDate}
					/>
					<RequesterBlock
						assetOwnerId={request.assetOwner.id}
						companyName={
							isAo
								? request.networkOperator.name
								: request.assetOwner?.name
						}
						isAO={isAo}
						mnoId={request.networkOperator.id}
						user={request.networkOperatorUser}
					/>
				</div>
				<RequestTable
					acceptAssetRentRequest={openAcceptModal}
					contractAssetRentRequest={openContractModal}
					doneAssetRentRequest={openDoneModal}
					openProposeDatesModal={openProposeDatesModal}
					reFetchRequestCard={reFetchRequestCard}
					request={request}
					role={role}
					sendRequestToAO={openSendToAOModal}
					sendRequestToMNO={openSendToMNOModal}
				/>
				<Modal
					isOpen={isDeclineModalOpen}
					onClose={closeDeclineModal}
					title='Confirm Action'
				>
					<RequestActionConfirmationModal
						acceptButtonTitle={isAo ? 'Decline request' : 'Cancel request'}
						cancelButtonTitle='Close'
						closeModal={closeDeclineModal}
						description={`Are you sure you want to ${isAo ? 'decline' : 'cancel'} the request?`}
						isCommentRequired={true}
						label='Please provide the reason'
						onApprove={confirmDecline as TOnApprove}
						placeholder='Enter reason'
					/>
				</Modal>

				<Modal
					isOpen={isProposeDatesModal && isAo}
					onClose={closeProposeDatesModal}
					title='Change Dates'
				>
					<ProposeNewDatesForItem
						closeModal={closeProposeDatesModal}
						proposeNewDatesForAsset={confirmNewDateProposition as unknown as TProposeNewDatesForItemParams}
						requestId={request.id}
						selectedAssetId={selectedItemForChangeDates}
					/>
				</Modal>

				<Modal
					isOpen={isSendToAOModal && !isAo}
					onClose={closeSendToAOModal}
					title='Send to Asset Owner'
				>
					<RequestActionConfirmationModal
						acceptButtonTitle='Send'
						closeModal={closeSendToAOModal}
						label='Comments for Asset Owner'
						onApprove={handleSendToAO as TOnApprove}
						placeholder='Add comments for Asset Owner'
					/>
				</Modal>

				<Modal
					isOpen={isSendToMNOModal && isAo}
					onClose={closeSendToMNOModal}
					title='Send to Asset Consumer'
				>
					<RequestActionConfirmationModal
						acceptButtonTitle='Send'
						closeModal={closeSendToMNOModal}
						label='Comments for Asset Consumer'
						onApprove={handleSendToMNO as TOnApprove}
						placeholder='Add comments'
					/>
				</Modal>

				<Modal
					isOpen={isAcceptModalOpen}
					onClose={closeAcceptModal}
				>
					<RequestActionConfirmationModal
						acceptButtonTitle='Send'
						closeModal={closeAcceptModal}
						isCommentRequired={false}
						onApprove={acceptAssetRentRequest as TOnApprove}
					/>
				</Modal>

				<Modal
					isOpen={isContractModalOpen}
					onClose={closeContractModal}
				>
					<RequestActionConfirmationModal
						acceptButtonTitle='Send'
						closeModal={closeContractModal}
						isCommentRequired={false}
						onApprove={contractAssetRentRequestHandler as TOnApprove}
					/>
				</Modal>

				<Modal
					isOpen={isDoneModalOpen}
					onClose={closeDoneModal}
				>
					<RequestActionConfirmationModal
						acceptButtonTitle='Send'
						closeModal={closeDoneModal}
						isCommentRequired={false}
						onApprove={doneAssetRentRequestHandler as TOnApprove}
					/>
				</Modal>

				<Modal
					height='80vh'
					isOpen={isShowDocumentsTable}
					maxWidth='1120px'
					onClose={closeAttachedDocumentsTable}
					title='Documents'
					width='80vw'
				>
					<AttachedContractDocuments
						isAo={isAo}
						isEditAccess={isDocumentsEditAccess}
						requestId={request.id}
					/>
				</Modal>
			</>
			}
			{isLoading && <SpinnerOverlay />}
		</div>
	);
};

export default memo(RequestReservationCard);
